import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
/* LES TYPES SEULEMENT, ET C'EST UNE CONTRAINTE DE GRAPHE, pas une preference :
   `EnfantDeModele` et `LinkedDoor` vivent dans le CONTRAT (aucune valeur, aucun
   import), et `LiensPort` vit dans `lib/liens.tsx`, qui importe `views/screens.ts`.
   Un import de VALEUR depuis `liens.tsx` fermerait un cycle le jour ou
   l'assemblage lirait les modeles. Ce fichier ne prend donc que des types, qui
   s'effacent a la compilation (`verbatimModuleSyntax`). */
import type { EnfantDeModele, LinkedDoor } from '../views/contract.js';
import type { LiensPort } from './liens.js';

/* Les enfants d'un modele se RE-EXPORTENT d'ici : `index.ts` sort les modeles
   depuis un seul fichier, et le type vit avec `LinkedDoor` parce qu'un enfant
   se reconnait par le meme vocabulaire qu'un lien (`views/contract.ts`). */
export type { EnfantDeModele } from '../views/contract.js';

/**
 * LES MODELES DE FICHES, cote SOCLE : le PORT, et les regles PURES des enfants.
 *
 * CE QUE CE FICHIER NE FAIT PAS, ET POURQUOI. Il n'importe pas `@taki/sdk` et
 * il n'en importera jamais : l'ordre de build est sdk -> ui -> shell -> apps,
 * et le socle d'interface ne peut pas dependre de ce qui est construit apres
 * lui. C'est la meme raison qui tient deja `views/fields.ts` a distance du SDK.
 * Le socle decrit donc la FORME du service (six verbes, un modele et ses
 * enfants), l'HOTE la remplit avec le repository reel (`sdk.templates`), et les
 * deux gestes de l'ecran ne connaissent que cette forme.
 *
 * LES DEUX GESTES QUI S'EN SERVENT, tous deux tenus par le socle :
 *  - « Enregistrer comme modele », dans le menu "..." de la rangee 3c d'une
 *    fiche ouverte (le moteur le calcule, comme Archiver et Supprimer) ;
 *  - « Nouveau selon un modele », derriere la fleche de « Ajouter » en rangee
 *    3a, qui OUVRE la fiche en creation, pre-remplie par le modele (regle G6,
 *    depuis le 23/09/2026) : c'est « Creer » qui ecrit, par `createFrom`. Aucune
 *    app n'ecrit une ligne pour l'un ni pour l'autre.
 *
 * ET DEPUIS LE MODE MODELES, UN TROISIEME USAGE, le seul qui LISE le contenu
 * d'un modele : l'ecran bascule sa liste sur les modeles de la porte, les
 * projette comme des fiches (`pseudoFicheDeModele`) et les rend MODIFIABLES
 * (`update`). Le moteur (`views/useScreen.ts`) y remplace le `set()` du binding
 * de l'app par le port : c'est le meme geste a l'ecran, il n'ecrit simplement
 * pas au meme endroit.
 *
 * UN MODELE EMPORTE AUSSI SES FICHES LIEES (chantier `modeles-enfants`) :
 * « Enregistrer comme modele » sur un materiel retient ses interventions, et
 * « Creer », sur la fiche ouverte depuis ce modele, recree les deux d'un coup
 * (la fiche en creation les montre en apercu dans ses cartes liees). Ces ENFANTS vivent
 * DANS le document du modele (`children`), ils ne sont des fiches nulle part.
 *
 * D'OU LA REGLE DE LA CARTE LIEE EN MODE MODELES : sur la fiche d'un modele,
 * elle ne demande RIEN au port des liens (aucune lecture, aucun compte serveur).
 * Elle trie `children` par lien (`enfantsDuLien`), les projette par le binding
 * ORDINAIRE de la porte liee (`pseudoFicheDEnfant`) et calcule ses totaux sur ce
 * tableau (`totalDesEnfants`). Demander au serveur les fiches liees d'un modele
 * rendrait toujours zero : aucune fiche ne porte l'id d'un modele.
 *
 * ET UN ENFANT S'OUVRE COMME UNE FICHE, DANS SA PORTE (pivot du fondateur,
 * 10/09/2026). La carte liee d'un modele reste une LISTE en lecture : cliquer
 * une ligne ouvre l'enfant dans la porte liee, ou il se modifie case par case
 * par le chemin ORDINAIRE du moteur. Il lui faut donc une ADRESSE, et c'est la
 * clef `id` existante qui la porte : `<modeleId>/<enfantId>` (`cheminDEnfant`,
 * `lireCheminDEnfant`). Le separateur est sur : un id Firestore ne contient
 * jamais `/`, et un id d'enfant est un UUID. Sans adresse, un enfant ne se
 * rechargerait pas (F5) et ne se partagerait pas.
 *
 * CE QUE LE SOCLE RETIRE AVANT D'ENVOYER AU PORT, et pourquoi c'est LUI qui le
 * fait : les champs qui REFERENCENT UNE AUTRE PORTE (les liens ENTRANTS,
 * `ScreenDoor.entrants`), sur la fiche racine comme sur chaque enfant
 * (`sansChampsDeLien`). Un modele est un PATRON : garder
 * `assetId: 'materiel-3'` dans un enfant accrocherait toutes les fiches creees
 * depuis ce modele au materiel d'origine, sans une seule erreur. Ces liens sont
 * une notion d'ECRAN (l'assemblage les deduit des colonnes de reference), le
 * SDK ne les connait pas. Le champ du lien lui-meme (`linkKey`) et les champs
 * standard, eux, sont retires par le SDK : lui seul sait ce qui ne se recopie
 * pas d'une fiche.
 *
 * LE PORT EST FACULTATIF, ET C'EST LE POINT. `useModelesPort()` rend `null`
 * quand personne ne l'a fourni, et les deux gestes n'en cassent pas pour
 * autant : ils lisent la meme raison (`raisonDesModeles`) et en font DEUX
 * choses differentes, decision du fondateur du 08/09/2026. « Enregistrer comme
 * modele », entree d'un MENU qu'on parcourt, reste GRISE avec sa raison (G16,
 * G18), exactement comme un verbe d'archivage qu'une porte ne declare pas : la
 * phrase y nomme la clef manquante a qui la cherche. La fleche de « Ajouter »,
 * moitie d'un BOUTON qu'on vise, DISPARAIT : ces trois manques sont de la
 * configuration, aucun geste a l'ecran ne les leve, et une moitie de bouton
 * grisee a vie ne s'y lirait pas comme une explication mais comme une panne.
 * L'asymetrie est ecrite en entier dans `components/ScreenLayout.tsx`, au
 * commentaire de la rangee 3a.
 */

/**
 * UN MODELE, vu du socle : de quoi le NOMMER dans une liste, le rappeler par
 * son id, et depuis le MODE MODELES, le MONTRER et le MODIFIER comme une fiche.
 *
 * LES DEUX DERNIERS CHAMPS SONT FACULTATIFS, ET C'EST EXACT : le menu de la
 * fleche de « Ajouter » n'a jamais eu besoin que du nom, et il ne doit pas
 * cesser de marcher pour une reponse d'hote qui n'en dirait pas plus. Ils ne
 * demandent d'ailleurs rien de neuf a l'hote, qui relaie deja des `Template`
 * complets : c'est le socle qui cesse d'en ignorer deux clefs.
 */
export interface ModeleDeFiche {
  /** Id stable du modele, celui qu'on repasse a `createFrom` et a `archive`. */
  id: string;
  /** Le nom saisi a l'enregistrement, celui qu'on lit dans la liste. */
  name: string;
  /** Une ligne de contexte sous le nom. Absente ou nulle : la ligne ne se rend pas. */
  description?: string | null;
  /**
   * LA FAMILLE VISEE par le modele, telle que le SDK la range (`assets`,
   * `careRecords`) : le repository, pas le sous-type. Le nom vient du document
   * (`Template.type`), et l'hote le relaie sans rien traduire.
   *
   * A QUOI ELLE SERT, ET IL N'Y A QUE CA : RETROUVER LA PORTE DU MODELE PARENT
   * quand on entre A FROID sur l'adresse d'un enfant (F5, lien colle). L'ecran
   * n'a alors AUCUNE memoire de la provenance, et la seule facon de nommer la
   * porte ou vit le modele est de comparer son jeu au sien
   * (`porteParenteDUnEnfant`, `views/useScreen.ts`). Le sous-type seul ne
   * suffit pas : deux portes de familles differentes peuvent le partager.
   *
   * FACULTATIVE COMME LES AUTRES : un hote qui ne relaierait que le nom et l'id
   * continue de marcher, et la puce de sortie retombe alors sur « Liste ».
   */
  type?: string;
  /**
   * LE SOUS-TYPE VISE par le modele (`materiel`, `vehicule`...). C'est la clef
   * du tri de `filtrerModelesParType` : le repository range par FAMILLE, et
   * deux apps d'une meme famille ne doivent jamais voir les modeles l'une de
   * l'autre. Il devient aussi le `type` de la pseudo-fiche du mode Modeles, la
   * ou une fiche reelle porterait le sien.
   */
  targetType?: string;
  /**
   * LES CHAMPS ENREGISTRES DANS LE MODELE, tels que le SDK les a copies de la
   * fiche d'origine. Le socle ne les INTERPRETE pas : il les projette par le
   * binding ORDINAIRE de la porte (voir `pseudoFicheDeModele`), donc il n'a
   * besoin de connaitre ni leurs clefs, ni leurs types.
   */
  fields?: Record<string, unknown>;
  /**
   * LES FICHES LIEES QUE LE MODELE EMPORTE, telles que `sdk.templates` les range
   * (`children`). Une par fiche a recreer sous la racine, avec le CHEMIN du
   * champ qui recevra l'id de cette racine (`linkKey`).
   *
   * ABSENT EST LE CAS ORDINAIRE, et le socle lit donc toujours `children ?? []` :
   * les modeles d'avant ce chantier n'en portent pas, et un modele enregistre
   * depuis un BROUILLON n'en porte pas non plus (rien n'etait encore lie a une
   * fiche qui n'existait pas). Une carte liee vide en mode Modeles n'est pas une
   * panne, c'est un modele qui n'emporte rien.
   */
  children?: EnfantDeModele[];
  /**
   * LES PLANS RECURRENTS QUI CITENT CE MODELE (leurs ids), tels que le SDK les
   * tient (`Template.plansIds`, chantier `plan-sur-modele`). Le socle n'en lit
   * que le NOMBRE : tant qu'il n'est pas nul, le modele ne se retire pas
   * (`raisonDeRetraitDuModele`). Absent : aucun plan, le cas ordinaire.
   */
  plansIds?: string[];
}

/**
 * POURQUOI CE MODELE NE SE RETIRE PAS, ou `null`.
 *
 * UN PLAN RECURRENT RELIT SON MODELE A CHAQUE PASSAGE : retire, il n'aurait plus
 * rien a produire, et le serveur le suspendrait la nuit suivante. Le SDK et les
 * Security Rules refusent deja l'archivage ; le socle GRISE les deux entrees
 * « Retirer » avec cette phrase (le menu de la fleche de « Ajouter », le menu
 * « ... » d'un modele ouvert), au lieu de laisser un clic se solder par un refus
 * muet. La phrase est COURTE, c'est un survol : celle du SDK
 * (`raisonDeProtectionDuModele`) dit la meme chose, en plus long, a qui code.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : un pluriel qui se trompe ne fait rougir
 * aucun build.
 */
export function raisonDeRetraitDuModele(modele: Pick<ModeleDeFiche, 'plansIds'>): string | null {
  const nombre = modele.plansIds?.length ?? 0;
  if (nombre === 0) return null;
  return `Utilisé par ${String(nombre)} plan(s) récurrent(s)`;
}

/**
 * CE QUE L'ECRAN ENVOIE POUR UN ENFANT A L'ENREGISTREMENT : la fiche liee TELLE
 * QUELLE, champs standard compris, comme la fiche racine part telle quelle.
 *
 * POURQUOI CE N'EST PAS DEJA UN `EnfantDeModele`. Le tri de ce qui ne se recopie
 * pas d'une fiche (l'id, les dates, l'auteur, le champ du lien) appartient au
 * SDK (`templateChildFromRecord`), qui seul connait les champs standard d'une
 * famille. Le socle, lui, retire ce que le SDK ne peut pas connaitre : les
 * champs qui referencent une AUTRE porte de l'ecran (voir l'en-tete). Deux
 * tris, deux savoirs, et aucun des deux ne devine ce que l'autre sait.
 */
export interface EnfantAEnregistrer {
  /** Le repository du SDK, au PLURIEL (`careRecords`), comme `jeu.family`. */
  family: string;
  /**
   * Le sous-type de la porte liee, quand elle en declare un, sinon `''` : c'est
   * alors le `type` porte par `record` qui gagne, cote SDK. Un enfant sans
   * sous-type d'aucun cote y est refuse, et il vaut mieux : il creerait une
   * fiche que sa propre porte ne montrerait pas.
   */
  type: string;
  /** Le CHEMIN DE STOCKAGE du champ de lien (`assetId`, `customFields.materielId`). */
  linkKey: string;
  /** La fiche liee telle que le port des liens vient de la lire. */
  record: Record<string, unknown>;
}

/**
 * COMBIEN DE FICHES LIEES UN MODELE EMPORTE, AU PLUS.
 *
 * POURQUOI UN PLAFOND, ET POURQUOI ICI. Un modele est un patron qu'on remplit a
 * la main : personne n'en cree un depuis un materiel qui porte 4000
 * interventions. Sans plafond, « Enregistrer comme modele » lirait la
 * collection entiere, ecrirait un document Firestore qui deborde son megaoctet,
 * et l'echec arriverait au moment de l'ECRITURE, apres l'attente, avec un
 * message qui ne parle de rien. C'est le meme plafond que celui d'une carte
 * liee, et pour la meme raison : on charge une fenetre, jamais un jeu.
 */
export const PLAFOND_ENFANTS_DE_MODELE = 200;

/**
 * UN MODELE, VU COMME UNE FICHE. La forme que le mode Modeles donne a projeter
 * au binding de la porte, sans qu'aucune app n'ecrive une ligne pour ca.
 *
 * L'ORDRE DES SPREADS EST LE FOND DE CETTE FONCTION, pas une preference
 * d'ecriture : `id` et `type` viennent EN DERNIER, donc ils gagnent toujours.
 * Les `fields` d'un modele sont une copie d'une fiche reelle, faite par le SDK
 * ou saisie a la main dans une console d'administration : rien n'empeche un
 * `id` de s'y trouver. S'il l'emportait, DEUX modeles differents sortiraient
 * sous le MEME id, la liste n'en montrerait qu'un, et une modification faite
 * sur l'un partirait chez l'autre. Aucun ecran ne rougirait.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule regle du mode qui puisse se
 * tromper en silence, et un test ne peut la tenir que si elle est atteignable
 * sans React et sans SDK.
 */
export function pseudoFicheDeModele(modele: ModeleDeFiche): Record<string, unknown> {
  return { ...modele.fields, id: modele.id, type: modele.targetType };
}

/**
 * UN ENFANT, VU COMME UNE FICHE. Ce que la carte liee projette en mode Modeles,
 * par le binding ORDINAIRE de la porte liee : la ligne d'un enfant se rend donc
 * exactement comme la ligne d'une vraie fiche liee.
 *
 * MEME ORDRE DE SPREADS, MEME RAISON QUE `pseudoFicheDeModele` : `id` et `type`
 * viennent EN DERNIER, donc ils gagnent. Les `fields` d'un enfant sont une copie
 * d'une fiche reelle faite par le SDK, ou une saisie de console : rien n'empeche
 * un `id` de s'y trouver. S'il l'emportait, deux enfants sortiraient sous le
 * MEME id, la selection en cocherait deux d'un coup, une ecriture de case
 * partirait chez l'autre, et un « Retirer » en effacerait un que personne n'a
 * choisi. Aucun ecran ne rougirait.
 *
 * `type` EST TOUJOURS POSE, contrairement au modele dont le `targetType` peut
 * manquer : un enfant en porte un par construction (`EnfantDeModele.type`), et
 * c'est ce que la fiche creee portera.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : le moteur la rappelle a chaque ecriture de
 * case (`champsApresEcriture` relit la valeur DANS cette pseudo-fiche), donc les
 * deux surfaces doivent lire exactement la meme forme, sans en garder de copie.
 */
export function pseudoFicheDEnfant(enfant: EnfantDeModele): Record<string, unknown> {
  return { ...enfant.fields, id: enfant.id, type: enfant.type };
}

/**
 * CE QUE L'HOTE FOURNIT. Six verbes : lister, enregistrer depuis une fiche,
 * creer une fiche, retirer un modele, et depuis le MODE MODELES, modifier un
 * modele et en creer un vide.
 *
 * `family` ET `type` VOYAGENT ENSEMBLE PARTOUT, parce qu'ils ne disent pas la
 * meme chose : `family` est le repository du SDK (ex. `assets`), `type` est le
 * sous-type que la porte montre (ex. `materiel`). Deux apps qui rangent leurs
 * fiches dans la meme famille ne doivent JAMAIS voir les modeles l'une de
 * l'autre : c'est le `type` qui les separe, et c'est pour ca qu'il accompagne
 * la famille jusque dans `list`.
 */
export interface ModelesPort {
  /** Les modeles de CE jeu, du plus utile au moins utile pour l'hote. */
  list(family: string, type: string): Promise<ModeleDeFiche[]>;
  /**
   * UN MODELE PAR SON ID, ou `null` quand il n'existe plus.
   *
   * POURQUOI CE VERBE ARRIVE AVEC LE PIVOT DU 10/09/2026. Un ENFANT s'ouvre
   * desormais comme une fiche, DANS LA PORTE LIEE (`s=interventions`,
   * `id=<modeleId>/<enfantId>`) : cette porte-la ne liste pas les modeles du
   * materiel, et un F5 sur cette adresse n'a donc AUCUNE liste ou retrouver le
   * parent. Le socle le lit par son id, seul, et affiche « chargement » en
   * attendant plutot que « Fiche introuvable ».
   *
   * `null` N'EST PAS UNE ERREUR : le modele a pu etre retire depuis un autre
   * onglet. L'ecran dit alors « Fiche introuvable », comme pour une fiche.
   */
  get(templateId: string): Promise<ModeleDeFiche | null>;
  /**
   * Enregistre la fiche OUVERTE comme modele. `record` part TEL QUEL, champs
   * standard compris : c'est le SDK qui sait lesquels ne se recopient pas
   * (l'id, les dates, l'auteur). Le socle ne trie pas ce qu'il ne connait pas.
   */
  saveFromRecord(args: {
    family: string;
    type: string;
    record: Record<string, unknown>;
    name: string;
    /**
     * LES FICHES LIEES A EMPORTER, deja lues par le socle
     * (`enfantsAEnregistrer`) et deja debarrassees de leurs references vers une
     * autre porte. Le SDK y retire le reste et pose un id par enfant.
     *
     * ABSENT VEUT DIRE « AUCUNE LECTURE N'A EU LIEU » (une porte sans lien, un
     * brouillon, un hote sans port des liens), pas « aucune fiche liee » : le
     * modele s'enregistre quand meme, il n'emporte simplement rien. Ne jamais
     * REFUSER l'enregistrement pour ca : le geste marchait avant ce chantier et
     * doit continuer sans que rien ne soit declare.
     */
    children?: EnfantAEnregistrer[];
  }): Promise<ModeleDeFiche>;
  /**
   * CREER LA FICHE, ET SES FICHES LIEES, DEPUIS UN MODELE, et rendre son id.
   *
   * APPELE PAR « Creer », JAMAIS PAR LE MENU (23/09/2026) : choisir un modele
   * ouvre la fiche en creation, pre-remplie (le brouillon de la regle G6), et
   * c'est seulement au clic sur « Creer » que cette fiche part, avec ce que
   * l'utilisateur y a corrige.
   *
   * `fields` EST LA FICHE ENTIERE DU BROUILLON (`champsDuBrouillon`), sans `id`
   * ni `type`, JAMAIS UN DELTA : l'instanciation pose ces champs APRES ceux du
   * modele et fusionne `customFields` par REMPLACEMENT, donc un delta effacerait
   * le sac du modele. Envoyee entiere, une case VIDEE a l'ecran le reste sur la
   * fiche creee (la valeur du modele ne revient pas). `type` voyage a part,
   * comme dans `CreationPort.create` : c'est la porte qui le connait.
   */
  createFrom(input: {
    templateId: string;
    type: string;
    fields: Record<string, unknown>;
  }): Promise<{ id: string }>;
  /** Retire un modele de la liste (archivage cote SDK, jamais une suppression dure). */
  archive(templateId: string): Promise<void>;
  /**
   * MODIFIER UN MODELE : son nom, sa description, ses champs. Un patch PARTIEL,
   * chaque clef absente laissant la valeur en place.
   *
   * POURQUOI CE VERBE ARRIVE MAINTENANT. Les quatre premiers suffisaient tant
   * qu'un modele n'etait qu'une entree de menu : on l'enregistrait depuis une
   * fiche, on s'en servait, on le retirait. Le MODE MODELES demande a le VOIR
   * et a le MODIFIER, et c'est exactement la demande du fondateur : « on peut
   * les voir et les MODIFIER ou en AJOUTER ».
   *
   * `fields` PART EN ENTIER, jamais en delta : le socle refusionne la map avant
   * d'appeler (l'ecriture d'une case rend `{ ...champs, [clef]: valeur }`).
   * C'est le seul contrat qui marche sans que l'hote ait a deviner ce qu'un
   * chemin pointe designe.
   */
  update(
    templateId: string,
    patch: {
      name?: string;
      description?: string;
      fields?: Record<string, unknown>;
      /**
       * LES ENFANTS, LE TABLEAU ENTIER, jamais un delta : meme contrat que
       * `fields`, et pour la meme raison. Les trois gestes du mode Modeles
       * (modifier une case d'enfant, en ajouter un, en retirer) rendent chacun
       * le tableau complet (`enfantModifie`, `avecEnfant`, `sansEnfants`), et
       * l'hote l'ecrit tel quel.
       *
       * UN TABLEAU VIDE N'EST PAS UNE CLEF ABSENTE, et la difference se paie
       * cher : `[]` RETIRE le dernier enfant (c'est le geste « Retirer »), la
       * clef absente laisse les enfants EN PLACE. Confondre les deux effacerait
       * les fiches liees d'un modele a chaque renommage, sans un mot.
       */
      children?: EnfantDeModele[];
    },
  ): Promise<void>;
  /**
   * CREER UN MODELE dans ce jeu : son nom, et les champs deja saisis.
   *
   * `family` ET `type` VOYAGENT ENSEMBLE, comme dans `list` et
   * `saveFromRecord`, et pour la meme raison : sans le sous-type, le modele
   * cree serait celui de toute la famille et apparaitrait chez le voisin.
   *
   * `fields` ARRIVE AVEC LE PIVOT DU 10/09/2026 : un modele ne se cree plus par
   * une fenetre a champ unique, mais par une FICHE VIDE (le brouillon
   * `~nouveau`, regle G6). Le titre saisi devient `name`, les autres cases
   * deviennent `fields` (`modeleDuBrouillon`). Absent = un modele nu, comme
   * avant : l'hote pose `{}`.
   *
   * ET IL REND L'ID, pour la meme raison : le socle OUVRE ce qu'il vient de
   * creer, comme apres « Creer » sur une fiche. Sans l'id, il faudrait
   * reconnaitre le modele neuf a son nom dans la liste relue, c'est-a-dire
   * ouvrir le mauvais des deux le jour ou deux modeles portent le meme.
   */
  create(input: {
    family: string;
    type: string;
    name: string;
    fields?: Record<string, unknown>;
  }): Promise<{ id: string }>;
}

/**
 * LE CONTEXTE, DEFAUT `null`, et ce defaut est LU. Un port absent est un fait
 * ordinaire (une app montee sans hote, un test, un shell sans SDK) : il eteint
 * les deux gestes avec leur raison, il ne casse rien.
 */
const ModelesContext = createContext<ModelesPort | null>(null);

/**
 * Fournisseur pose par l'HOTE (celui qui a le SDK), autour du shell. Meme patron
 * que `PendingActionProvider` : le socle decrit, l'exterieur remplit.
 */
export function ModelesProvider({
  value,
  children,
}: {
  value: ModelesPort | null;
  children: ReactNode;
}): JSX.Element {
  return <ModelesContext.Provider value={value}>{children}</ModelesContext.Provider>;
}

/** Le port des modeles, ou `null` quand l'hote n'en fournit pas. */
export function useModelesPort(): ModelesPort | null {
  return useContext(ModelesContext);
}

/**
 * LES TROIS RAISONS QUI ETEIGNENT LES MODELES, ECRITES ICI ET NULLE PART
 * AILLEURS. Les deux gestes vivent dans deux fichiers qui ne se voient pas (le
 * moteur `views/useScreen.ts` pour « Enregistrer comme modele », le chassis
 * `components/ScreenLayout.tsx` pour la fleche de « Ajouter ») : chacun gardait
 * sa copie de la phrase, et deux copies d'un meme fait divergent. Elles vivent
 * donc dans le PORT, que les deux importent deja.
 *
 * POURQUOI TROIS, ET PAS UNE. Il manque trois choses INDEPENDANTES, et une
 * phrase unique enverrait chercher au mauvais endroit deux fois sur trois :
 *  - le SERVICE : l'hote monte un `ModelesProvider`, ou ne le monte pas ;
 *  - l'ADRESSE : la porte declare son `jeu` (la famille, le sous-type) ;
 *  - l'ACCORD : la porte ALLUME les modeles (`modeles: true`). Toutes n'en
 *    veulent pas, et celle qui n'en veut pas n'est pas cassee.
 *
 * AUCUNE DES TROIS NE CACHE L'ENTREE DU MENU « ... » (G16, G18) : un menu dont
 * les entrees vont et viennent selon l'app fabrique exactement le « pourquoi je
 * ne peux pas faire ca ici » qu'on veut eviter. Les trois CACHENT en revanche
 * la fleche de « Ajouter » depuis le 08/09/2026 : elles disent toutes les trois
 * une CONFIGURATION, et la frontiere du chassis est configuration => absence,
 * etat => grisage, donnees => jamais. Voir l'en-tete de ce fichier.
 */
export const RAISONS_MODELES = {
  /** Personne n'a monte de `ModelesProvider` au-dessus de l'ecran. */
  sansPort: "L'hote ne fournit pas les modeles.",
  /** L'app n'a pas declare `jeu` sur la porte : on ne sait pas ou ranger le modele. */
  sansJeu: 'Cette app ne declare pas le jeu de la porte.',
  /** Le service et l'adresse sont la, mais l'app n'a pas allume les modeles ici. */
  sansActivation:
    'Les modeles ne sont pas actives sur cette porte : `modeles: true` dans taki.screens.json.',
} as const;

/**
 * LES MODELES SONT-ILS DISPONIBLES SUR CETTE PORTE, ET SINON POURQUOI ? Rend la
 * PREMIERE chose qui manque, ou `null` quand les trois sont la.
 *
 * L'ORDRE EST LA REGLE, PAS UN DETAIL D'ECRITURE : port, puis jeu, puis
 * activation. Il va du plus general au plus local, donc de ce que le lecteur ne
 * peut pas changer vers ce qu'il change en une ligne. Repondre « active les
 * modeles sur cette porte » a quelqu'un dont le shell ne fournit aucun port
 * l'envoie editer un fichier qui n'y changera rien.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : les DEUX gestes du socle l'appellent au
 * lieu de refaire le calcul chacun, et un test tient cet ordre sans React, sans
 * DOM et sans SDK.
 */
export function raisonDesModeles(etat: {
  /** Le port de l'hote (`useModelesPort()`), `null` quand personne ne le fournit. */
  port: ModelesPort | null;
  /** Le jeu de la porte active (`ScreenDoorState.jeu`), `null` si elle n'en declare pas. */
  jeu: { family: string; type?: string } | null;
  /** La porte allume-t-elle les modeles (`ScreenDoorState.modeles`) ? */
  modeles: boolean;
}): string | null {
  if (etat.port == null) return RAISONS_MODELES.sansPort;
  /* UNE FAMILLE VIDE EST UN JEU ABSENT : le port range par famille, et
     `list('', type)` demanderait « les modeles de rien ». */
  if (etat.jeu == null || etat.jeu.family === '') return RAISONS_MODELES.sansJeu;
  if (!etat.modeles) return RAISONS_MODELES.sansActivation;
  return null;
}

/**
 * LE FILTRE PAR SOUS-TYPE, cote client, et pourquoi il vit ICI.
 *
 * Le repository des modeles est range par FAMILLE : `list({ type: family })` en
 * rend donc autant que la famille en porte, tous sous-types confondus. Le
 * sous-type de la porte (`targetType`) n'est pas une clef du repository, il se
 * lit sur chaque modele : le tri se fait donc apres la lecture, une fois pour
 * toutes, dans une fonction PURE que l'hote appelle et qu'un test peut casser.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule regle de ce chantier qui
 * puisse se tromper en silence (montrer a une app les modeles d'une autre app
 * de la meme famille), et un test ne peut la tenir que si elle est atteignable
 * sans React et sans SDK.
 */
export function filtrerModelesParType<M extends ModeleDeFiche & { targetType?: string }>(
  modeles: readonly M[],
  type: string,
): M[] {
  return modeles.filter((modele) => modele.targetType === type);
}

/* ------------------------------------------------------------------ */
/* LES ENFANTS D'UN MODELE : les regles pures                          */
/* ------------------------------------------------------------------ */

/*
 * CE QUI SUIT EST PUR ET EXPORTE, sans exception, et c'est la meme raison a
 * chaque fois : un enfant se trompe EN SILENCE. Un champ range au mauvais etage
 * (racine au lieu du sac), un lien entrant oublie dans un patron, un id inconnu
 * qui vide une liste, un total calcule sur les mauvaises fiches : rien de tout
 * ca ne fait rougir un ecran. Le moteur (`views/useScreen.ts`) et la carte liee
 * (`views/core/LinkedCard.tsx`) les appellent tous les deux, et un test
 * `node --test` les tient sans React, sans DOM et sans SDK.
 */

/**
 * LE SAC OU VIT UN CHAMP SUR-MESURE, REDIT ICI et non importe de l'assemblage.
 *
 * MEME CHOIX QUE `lib/creation.tsx`, et pour la meme raison de graphe : ce
 * fichier ne prend de `views/` que des TYPES, qui s'effacent a la compilation.
 * Importer `SAC_SUR_MESURE` (une VALEUR de `views/screens.ts`) ferait dependre
 * le port des modeles de l'assembleur d'ecran, alors que l'assembleur est ce qui
 * pourra un jour lire les modeles. Le prix est cette ligne, et le jour ou le
 * prefixe bouge, `pnpm --filter @taki/ui test` le dit ici comme ailleurs.
 */
const SAC = 'customFields.';

/**
 * LIT UN CHEMIN DE STOCKAGE dans une fiche : `status` a la racine,
 * `customFields.cout` dans le sac. `undefined` quand la clef n'y est pas.
 *
 * DEUX ESPACES, ET DEUX SEULEMENT (AGENTS.md section 7), comme partout dans le
 * socle. Lire `record['customFields.cout']` a plat rendrait `undefined` sur une
 * fiche parfaitement remplie : un total a zero, une case vide, aucune erreur.
 */
export function lireChemin(record: Record<string, unknown>, chemin: string): unknown {
  if (!chemin.startsWith(SAC)) return record[chemin];
  const sac = record['customFields'];
  if (typeof sac !== 'object' || sac === null || Array.isArray(sac)) return undefined;
  return (sac as Record<string, unknown>)[chemin.slice(SAC.length)];
}

/**
 * POSE UNE VALEUR A UN CHEMIN DE STOCKAGE, sans muter l'entree.
 *
 * LES CINQ MEMES LIGNES QUE `appliquerValeursImposees` (`lib/liens.tsx`) ET
 * `ficheVide` (`lib/creation.tsx`), RECOPIEES EXPRES : les deux vivent dans des
 * fichiers que celui-ci ne peut pas importer en VALEUR (`liens.tsx` importe
 * `views/screens.ts`, et le port des modeles doit rester en dessous de
 * l'assemblage). Trois copies d'une regle de cinq lignes, ca se surveille par
 * les tests des trois ; une dependance de graphe fermee en rond, ca ne se
 * repare qu'en deplacant des fichiers.
 *
 * LE SAC EST FUSIONNE, JAMAIS REMPLACE : les autres champs sur-mesure de la
 * fiche (ceux des autres apps du pack) survivent. C'est ce que le « + Ajouter »
 * d'une carte liee en mode Modeles demande, avec le titre saisi d'un cote et
 * les valeurs deja rangees de l'autre.
 */
export function poserChemin(
  record: Record<string, unknown>,
  chemin: string,
  valeur: unknown,
): Record<string, unknown> {
  if (!chemin.startsWith(SAC)) return { ...record, [chemin]: valeur };
  const precedent = record['customFields'];
  const sac =
    typeof precedent === 'object' && precedent !== null && !Array.isArray(precedent)
      ? (precedent as Record<string, unknown>)
      : {};
  return { ...record, customFields: { ...sac, [chemin.slice(SAC.length)]: valeur } };
}

/**
 * RETIRE DES CHEMINS DE STOCKAGE d'une fiche, sans la muter.
 *
 * A QUOI CA SERT, ET C'EST LA REGLE LA PLUS SILENCIEUSE DU CHANTIER : un modele
 * est un PATRON, il ne retient AUCUN lien vers une fiche precise. La fiche
 * racine et chaque enfant passent donc ici avec les liens ENTRANTS de leur porte
 * (`ScreenDoor.entrants`, les champs qui referencent une autre porte). Sans ce
 * passage, un modele de materiel garderait `customFields.chantierId`, et chaque
 * fiche creee depuis ce modele naitrait accrochee au chantier de l'original.
 * Elle apparaitrait dans SA carte liee, des mois plus tard, sans que rien n'ait
 * jamais echoue.
 *
 * LE SAC RESTE, MEME VIDE : `customFields: {}` est une fiche sans champ
 * sur-mesure, et retirer la clef elle-meme ferait diverger la forme des fiches
 * selon ce qu'on leur a enleve. Un chemin absent ne fait rien, et rien d'autre
 * n'est touche : c'est un retrait, pas un tri.
 */
export function sansChampsDeLien(
  record: Record<string, unknown>,
  chemins: readonly string[],
): Record<string, unknown> {
  /* AUCUN CHEMIN, AUCUNE COPIE : la fiche voyage telle quelle vers le port, et
     c'est le cas de toutes les portes qui ne declarent aucune reference. */
  if (chemins.length === 0) return record;
  const sortie = { ...record };
  for (const chemin of chemins) {
    if (!chemin.startsWith(SAC)) {
      delete sortie[chemin];
      continue;
    }
    const precedent = sortie['customFields'];
    if (typeof precedent !== 'object' || precedent === null || Array.isArray(precedent)) continue;
    const sac = { ...(precedent as Record<string, unknown>) };
    delete sac[chemin.slice(SAC.length)];
    sortie['customFields'] = sac;
  }
  return sortie;
}

/**
 * LES ENFANTS D'UN SEUL LIEN, dans l'ordre du modele.
 *
 * TROIS CLEFS COMPARENT, PAS UNE : la FAMILLE (le repository), le CHEMIN du
 * champ de lien (`linkKey`) et le SOUS-TYPE quand le lien en declare un. Une
 * porte peut en designer une autre par DEUX champs (un materiel « pose » et un
 * materiel « repris ») : comparer la seule famille melangerait les deux cartes,
 * chacune montrant les enfants de l'autre. Et deux portes d'un meme pack
 * partagent souvent une famille (`assets`), separees par leur seul sous-type.
 *
 * UN LIEN SANS SOUS-TYPE ACCEPTE TOUT, et ce n'est pas un relachement : c'est
 * une porte qui ne declare pas de sous-type, donc dont le jeu ENTIER est le jeu
 * lie. Exiger une egalite avec `undefined` y renverrait une carte vide.
 *
 * `children` ABSENT REND `[]` : un modele d'avant ce chantier, ou un modele
 * enregistre depuis un brouillon. La carte dit « Aucune fiche liee dans ce
 * modele », elle ne casse pas.
 */
export function enfantsDuLien(
  children: readonly EnfantDeModele[] | undefined,
  lien: { family: string; type?: string; by: string },
): EnfantDeModele[] {
  return (children ?? []).filter(
    (enfant) =>
      enfant.family === lien.family &&
      enfant.linkKey === lien.by &&
      (lien.type === undefined || enfant.type === lien.type),
  );
}

/**
 * UN ID D'ENFANT NEUF.
 *
 * POURQUOI LE SOCLE EN POSE UN, alors que le SDK le fait deja a l'ecriture :
 * l'ajout est OPTIMISTE. L'enfant s'affiche dans la carte avant que le port
 * n'ait repondu, et il doit deja etre adressable (selection, `saveState.target`,
 * pile d'annulation). Un id pose apres la reponse ferait une ligne qu'on ne peut
 * ni cocher ni modifier pendant l'aller-retour, et qui sauterait ensuite.
 *
 * `crypto.randomUUID` EXISTE PARTOUT OU CE CODE TOURNE (navigateurs, Node 22),
 * mais la lecture est GARDEE : ce module est atteint par les tests `node --test`
 * et par des environnements que personne n'a choisis. Le repli n'a pas besoin
 * d'etre cryptographique, il a besoin d'etre UNIQUE dans un tableau de 200
 * lignes.
 */
export function idDEnfant(): string {
  const source = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof source?.randomUUID === 'function') return source.randomUUID();
  return `e-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * LE SOUS-TYPE QU'UN LIEN IMPOSE A SES ENFANTS, ou `null` quand la porte liee
 * n'en declare pas.
 *
 * POURQUOI UNE FONCTION POUR UNE LIGNE : DEUX surfaces lisent cette regle a une
 * seconde d'intervalle et doivent lire LA MEME. La carte liee s'en sert pour ne
 * PAS proposer « + Ajouter » (le geste n'aboutirait jamais, voir
 * `LinkedCard.tsx`), `nouvelEnfant` s'en sert pour REFUSER de fabriquer un
 * enfant sans type. Le jour ou les deux tests divergent, le bouton est la et
 * l'ajout echoue a chaque clic.
 *
 * `''` VAUT ABSENT, et ce n'est pas de la coquetterie : c'est exactement ce que
 * le schema du modele refuse (`templateChildCoreFields.type: z.string().min(1)`,
 * `packages/data-model/src/template.ts`). Traiter la chaine vide comme un
 * sous-type ferait passer la garde et echouer l'ecriture.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : elle decide de la PRESENCE d'un bouton,
 * donc son defaut ne se lit nulle part comme une erreur.
 */
export function sousTypeDuLien(lien: { type?: string }): string | null {
  const type = lien.type;
  return type === undefined || type === '' ? null : type;
}

/**
 * UN ENFANT NEUF POUR CE LIEN, tel que le « + Ajouter » d'une carte liee le
 * fabrique en mode Modeles.
 *
 * LES TROIS NOMS VIENNENT DU LIEN, JAMAIS DE L'ECRAN : famille, sous-type et
 * chemin du champ de lien sont exactement ce que `enfantsDuLien` comparera pour
 * le retrouver. Les prendre ailleurs fabriquerait un enfant qui n'apparait dans
 * AUCUNE carte, ajoute pour de bon dans le document, invisible partout.
 *
 * UN LIEN SANS SOUS-TYPE LEVE, et la version d'avant (09/09/2026) disait le
 * CONTRAIRE DU CODE : elle posait `type: ''` en promettant que « le SDK
 * refusera d'instancier ». Faux, et pire. C'est l'ECRITURE DU MODELE qui refuse
 * (`templateChildCoreFields.type: z.string().min(1)`), donc `update` rejetait,
 * la fenetre « Ajouter » restait ouverte sur « L'ajout a echoue. Reessaie. »
 * pour toujours, et rien nulle part ne nommait la cause.
 *
 * ET LA CAUSE EST DE LA CONFIGURATION : la porte liee montre un jeu qui ne
 * declare pas de `type` (`jeu.type`, `taki.screens.json`), donc `liensDe` ne
 * pose pas `LinkedDoor.type` (`views/screens.ts`). Aucun geste a l'ecran ne la
 * leve : la carte ne PROPOSE donc pas l'ajout (`sousTypeDuLien`), et ce refus-ci
 * est le FILET, pour l'appelant qui n'aurait pas lu la regle.
 */
export function nouvelEnfant(
  lien: {
    family: string;
    type?: string;
    by: string;
    /** La porte liee, pour NOMMER celle qu'il faut aller corriger. Facultative. */
    door?: string;
  },
  fields: Record<string, unknown>,
): EnfantDeModele {
  const type = sousTypeDuLien(lien);
  if (type === null) {
    throw new Error(
      `Le lien vers la porte "${lien.door ?? lien.family}" ne declare pas de sous-type : ` +
        "un enfant de modele en exige un. Declare `jeu.type` sur cette porte " +
        '(taki.screens.json), sans quoi aucune fiche liee ne peut etre ajoutee a un modele.',
    );
  }
  return {
    id: idDEnfant(),
    family: lien.family,
    type,
    linkKey: lien.by,
    fields,
  };
}

/**
 * LE TABLEAU AVEC UN ENFANT DE PLUS, EN FIN.
 *
 * EN FIN, ET C'EST UN CHOIX : la carte liee ne trie pas les enfants d'un modele
 * autrement que par leur ordre d'ecriture, donc l'ajout se voit LA OU ON VIENT
 * DE CLIQUER, en bas. Une insertion en tete ferait sauter la ligne neuve
 * au-dessus de tout ce qu'on regardait.
 *
 * UNE COPIE, JAMAIS UNE MUTATION : le tableau vient de l'etat React du moteur,
 * que la projection et la pile d'annulation relisent. Le muter ferait mentir un
 * pas d'annulation qui croit tenir l'ancien tableau.
 */
export function avecEnfant(
  children: readonly EnfantDeModele[] | undefined,
  enfant: EnfantDeModele,
): EnfantDeModele[] {
  return [...(children ?? []), enfant];
}

/**
 * LE TABLEAU SANS CES ENFANTS, dans l'ordre garde.
 *
 * UN ID INCONNU EST IGNORE, il ne vide rien et ne leve rien : la selection d'une
 * carte survit a un rechargement du modele, et l'enfant qu'elle designait a pu
 * partir entre-temps (un autre onglet, une console). Le geste « Retirer » retire
 * alors ce qui reste, ce qui est exactement ce que l'utilisateur voulait.
 */
export function sansEnfants(
  children: readonly EnfantDeModele[] | undefined,
  ids: readonly string[],
): EnfantDeModele[] {
  return (children ?? []).filter((enfant) => !ids.includes(enfant.id));
}

/**
 * LE TABLEAU AVEC LES `fields` D'UN ENFANT REMPLACES, l'ORDRE GARDE.
 *
 * L'ORDRE EST LE POINT : la carte liee montre les enfants dans l'ordre du
 * tableau, et une ecriture de case qui reclasse ferait sauter la ligne qu'on est
 * en train de modifier sous le curseur, a chaque frappe validee.
 *
 * `fields` PART EN ENTIER, comme partout dans ce port : c'est
 * `champsApresEcriture` qui a construit la map complete, en PROUVANT que la
 * valeur se relit la ou elle vient d'etre posee. Un delta obligerait l'hote a
 * deviner ce qu'un chemin designe.
 *
 * UN ID INCONNU NE CHANGE RIEN, et surtout n'ajoute pas d'enfant : une ecriture
 * sur un enfant disparu doit se perdre, pas fabriquer une ligne sans famille ni
 * lien que plus aucune carte ne montrerait.
 */
export function enfantModifie(
  children: readonly EnfantDeModele[] | undefined,
  id: string,
  fields: Record<string, unknown>,
): EnfantDeModele[] {
  return (children ?? []).map((enfant) => (enfant.id === id ? { ...enfant, fields } : enfant));
}

/**
 * UN TOTAL CALCULE SUR LES ENFANTS D'UN MODELE, EN MEMOIRE ET SANS SERVEUR.
 *
 * POURQUOI CE N'EST PAS UN AGREGAT, alors que la meme carte en demande un sur
 * une vraie fiche : les enfants d'un modele ne sont des fiches nulle part. Un
 * `count` serveur cadre par `{ [by]: id du modele }` rendrait TOUJOURS zero,
 * affiche comme un total, sur une carte qui montre trois lignes juste en
 * dessous. Le total se lit donc la ou vivent les enfants, et il n'est jamais
 * partiel : `children` EST le jeu entier (au plus `PLAFOND_ENFANTS_DE_MODELE`).
 *
 * SEULES LES VALEURS NUMERIQUES COMPTENT, les autres sont IGNOREES et ne valent
 * pas zero : une case vide, un texte laisse par une saisie de console ou une
 * date n'ont pas de somme, et les compter pour zero tirerait une moyenne vers le
 * bas sans que rien ne le dise.
 *
 * UNE SOMME SANS AUCUNE VALEUR VAUT 0, UNE MOYENNE VAUT `null`, et cette
 * asymetrie est le fond de la fonction : « rien a additionner » est bien zero,
 * « moyenne de rien » n'est pas zero, c'est une absence. Le rendre en 0
 * afficherait un chiffre qu'on croirait (meme regle que `LiensPort.average`).
 *
 * `field` ABSENT HORS `count` REND `null` : sans champ, il n'y a rien a sommer,
 * et 0 serait une reponse a une question qui n'a pas ete posee.
 */
export function totalDesEnfants(
  enfants: readonly EnfantDeModele[],
  agg: 'count' | 'sum' | 'avg',
  field?: string,
): number | null {
  if (agg === 'count') return enfants.length;
  if (field === undefined || field === '') return null;
  const nombres: number[] = [];
  for (const enfant of enfants) {
    const lu = lireChemin(enfant.fields, field);
    if (typeof lu === 'number' && Number.isFinite(lu)) nombres.push(lu);
  }
  const somme = nombres.reduce((total, valeur) => total + valeur, 0);
  if (agg === 'sum') return somme;
  return nombres.length === 0 ? null : somme / nombres.length;
}

/* ------------------------------------------------------------------ */
/* L'ADRESSE D'UN ENFANT (pivot du 10/09/2026)                         */
/* ------------------------------------------------------------------ */

/**
 * LE SEPARATEUR DE L'ADRESSE D'UN ENFANT, ecrit une fois.
 *
 * `/` EST SUR, ET CE N'EST PAS UN PARI : un id Firestore genere est
 * alphanumerique (20 caracteres, jamais de barre oblique), et un id d'enfant est
 * un UUID (`idDEnfant`). Aucune des deux moities ne peut donc contenir le
 * separateur, et la lecture est sans ambiguite meme sur un id retouche a la main
 * (`lireCheminDEnfant` refuse tout ce qui n'a pas exactement deux morceaux non
 * vides).
 */
const SEPARATEUR_DENFANT = '/';

/**
 * L'ADRESSE D'UN ENFANT : `<modeleId>/<enfantId>`, dans la clef `id` EXISTANTE.
 *
 * POURQUOI UN ENFANT S'ADRESSE PAR SON PARENT, et pas par son seul id. Un
 * enfant n'existe dans AUCUNE collection : il vit dans le tableau `children` du
 * document de son modele. Son id seul ne dit donc pas ou aller le chercher, et
 * le socle n'a aucun moyen de retrouver le modele qui le porte (il faudrait lire
 * tous les modeles de toutes les familles). Le chemin porte les deux, donc F5
 * suffit a le rouvrir : le moteur charge le parent par `ModelesPort.get`, puis y
 * retrouve l'enfant.
 *
 * PAS DE CLEF D'ADRESSE NOUVELLE, ET C'EST DELIBERE : une septieme clef aurait
 * traverse le contrat d'adresse (`SCREEN_SEARCH_KEYS`), le shell et sa
 * validation de route. La clef `id` dit deja « ce que l'ecran a ouvert », et un
 * enfant est exactement ca.
 *
 * FONCTION PURE ET EXPORTEE EXPRES, avec sa jumelle : une adresse est ce qui se
 * partage et ce qui se recharge, et deux recettes du meme chemin (l'une qui
 * ecrit, l'autre qui lit) divergeraient sans qu'un seul build rougisse.
 */
export function cheminDEnfant(modeleId: string, enfantId: string): string {
  return `${modeleId}${SEPARATEUR_DENFANT}${enfantId}`;
}

/**
 * L'ADRESSE D'UN ENFANT, RELUE : `{ modeleId, enfantId }`, ou `null`.
 *
 * `null` EST LA REPONSE ORDINAIRE, et c'est ce qui rend cette fonction utilisable
 * comme un AIGUILLAGE : le moteur la pose sur chaque `id` qu'il ouvre ou qu'il
 * ecrit, et un id de fiche ordinaire (sans barre oblique) rend `null` sans un
 * mot. Une moitie vide (`/e1`, `m1/`, `/`) rend `null` elle aussi : une adresse
 * retouchee a la main ne doit pas faire chercher un modele qui s'appelle `''`.
 *
 * PLUS DE DEUX MORCEAUX = REFUS : ni le modele ni l'enfant ne peuvent contenir
 * le separateur (voir `SEPARATEUR_DENFANT`), donc `a/b/c` n'est l'adresse de
 * rien, et deviner laquelle des deux coupures est la bonne serait inventer.
 */
export function lireCheminDEnfant(id: string): { modeleId: string; enfantId: string } | null {
  const morceaux = id.split(SEPARATEUR_DENFANT);
  if (morceaux.length !== 2) return null;
  const [modeleId, enfantId] = morceaux;
  if (modeleId == null || modeleId === '' || enfantId == null || enfantId === '') return null;
  return { modeleId, enfantId };
}

/**
 * LES FRERES D'UN ENFANT, EN ADRESSES : les enfants du MEME LIEN, dans l'ordre
 * du modele.
 *
 * C'EST LE CURSEUR DE LA FICHE D'UN ENFANT (« 1 / 2 », les fleches precedent et
 * suivant), et le jeu ne peut pas etre autre chose. Prendre TOUS les `children`
 * melangerait les interventions et les livraisons du meme modele : les fleches
 * changeraient de porte sous les doigts, sur une adresse que la porte affichee
 * ne sait pas rendre. Les trois clefs qui comparent sont celles
 * d'`enfantsDuLien` (famille, sous-type, chemin du champ de lien), lues sur
 * l'enfant OUVERT : c'est exactement le jeu que la carte du parent montrait.
 *
 * `[]` QUAND L'ENFANT N'EST PLUS LA : le curseur reste alors muet (`.. / ..`)
 * plutot que de designer une ligne au hasard.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : un curseur faux ne rougit nulle part, il
 * emmene simplement sur une autre fiche que celle qu'on visait.
 */
export function cheminsDesFreres(
  modeleId: string,
  children: readonly EnfantDeModele[] | undefined,
  enfantId: string,
): string[] {
  const ouvert = (children ?? []).find((enfant) => enfant.id === enfantId);
  if (ouvert === undefined) return [];
  return (children ?? [])
    .filter(
      (enfant) =>
        enfant.family === ouvert.family &&
        enfant.type === ouvert.type &&
        enfant.linkKey === ouvert.linkKey,
    )
    .map((enfant) => cheminDEnfant(modeleId, enfant.id));
}

/**
 * CE QU'UN BROUILLON DEVIENT QUAND IL CREE UN MODELE : son NOM, et ses CHAMPS.
 *
 * LE PIVOT DU 10/09/2026 : « + Ajouter » en mode Modeles n'ouvre plus la
 * fenetre « Nouveau modele », il ouvre la FICHE VIDE (regle G6). Le titre saisi
 * dans la fiche EST le nom du modele : c'est ce mot que la liste du mode
 * affiche (`nommerLignesDeModeles`), c'est lui qu'on renomme en editant le
 * titre, et un modele sans nom n'est plus retrouvable nulle part.
 *
 * LE CHEMIN DU TITRE NE SE RECOPIE PAS DANS `fields`, ET C'EST LA REGLE QUI SE
 * TROMPE EN SILENCE. Le SDK nomme la fiche creee depuis le NOM du modele
 * (`avecNomDeSecours`) : laisser `fields.name` a cote ferait deux sources pour
 * un seul mot, et renommer le modele laisserait l'ANCIEN nom dans ses champs,
 * donc dans toutes les fiches creees ensuite. Personne ne lirait ca comme un
 * bug : on lirait « le modele ne prend pas mon renommage ».
 *
 * `cheminDuTitre` ABSENT (role titre compose ou calcule) : rien n'est retire, et
 * le nom rendu est vide. C'est l'appelant qui refuse alors la creation, avec la
 * meme phrase que pour une fiche sans titre.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : elle decide de ce qu'un modele retient, et
 * un champ de trop ne se voit qu'a la premiere fiche creee depuis lui.
 */
export function modeleDuBrouillon(entree: {
  /** Les champs du brouillon, deja prives de `id` et `type` (`champsDuBrouillon`). */
  fiche: Record<string, unknown>;
  /** Le CHEMIN DE STOCKAGE du role titre de la porte (`name`, `customFields.objet`). */
  cheminDuTitre?: string;
}): { name: string; fields: Record<string, unknown> } {
  const chemin = entree.cheminDuTitre;
  if (chemin === undefined || chemin === '') return { name: '', fields: { ...entree.fiche } };
  const lu = lireChemin(entree.fiche, chemin);
  return {
    name: lu == null ? '' : String(lu).trim(),
    /* LE MEME RETRAIT QUE POUR UN LIEN ENTRANT, par la meme fonction : racine ou
       sac, et le sac reste meme vide. */
    fields: sansChampsDeLien(entree.fiche, [chemin]),
  };
}

/**
 * LES FICHES LIEES D'UNE FICHE, LUES POUR EN FAIRE LES ENFANTS D'UN MODELE.
 *
 * QUAND ELLE TOURNE : dans « Enregistrer comme modele », entre le nom saisi et
 * l'appel a `saveFromRecord`, sur une fiche REELLE dont la porte declare des
 * liens. Jamais sur un brouillon : rien n'y est encore lie a une fiche qui
 * n'existe pas.
 *
 * PAS PURE (elle appelle un port), MAIS TESTABLE : le port entre par
 * l'ARGUMENT, pas par un hook. Un faux port dans un test `node --test` tient le
 * cadrage, le plafond et la propagation des echecs, ce qu'aucun rendu ne
 * montrerait.
 *
 * LES FICHES ACTIVES SEULEMENT (`archived` absent, donc `'exclude'`) : un modele
 * est fait pour recreer un ETAT DE TRAVAIL. Emporter les interventions
 * archivees d'un materiel ferait renaitre, dans chaque fiche creee, des lignes
 * que quelqu'un avait justement rangees.
 *
 * EN PARALLELE, comme `comptesLies` : ces lectures vivent entre le clic et
 * l'enregistrement, leur duree est celle du plus LENT, pas celle de leur somme.
 *
 * UN LIEN QUI ECHOUE FAIT ECHOUER L'ENSEMBLE, et c'est deliberent : un modele
 * enregistre A MOITIE est pire qu'un enregistrement refuse. Il a l'air complet,
 * il porte le bon nom, et il recreera des fiches auxquelles il manque une porte
 * entiere, sans que personne ne puisse voir ce qui manque.
 *
 * `entrantsDe` VIENT DE L'APPELANT parce que le port des modeles ne connait pas
 * les portes de l'ecran : le moteur y lit `entrants` sur la porte LIEE
 * (`ScreenDoor.entrants`), c'est-a-dire les champs de cette porte qui
 * referencent une autre porte. Voir l'en-tete de ce fichier pour ce que ce
 * retrait ferme.
 */
export async function enfantsAEnregistrer(
  port: LiensPort,
  liens: readonly LinkedDoor[],
  id: string,
  entrantsDe: (door: string) => readonly string[],
): Promise<EnfantAEnregistrer[]> {
  const parLien = await Promise.all(
    liens.map(async (lien) => {
      const fiches = await port.list(lien.family, {
        ...(lien.type === undefined ? {} : { type: lien.type }),
        /* `by` EST DEJA UN CHEMIN DE STOCKAGE, pose par l'assemblage : meme
           egalite que `cadrageDeLien` (`lib/liens.tsx`), recopiee ici pour la
           meme raison de graphe que `poserChemin`. La rederiver en clef nue
           rendrait une liste vide sur un champ sur-mesure, donc un modele sans
           enfants, sans la moindre erreur. */
        where: { [lien.by]: id },
        limit: PLAFOND_ENFANTS_DE_MODELE,
      });
      /* LES ENTRANTS DE LA PORTE LIEE, pas ceux de la porte hote : ce sont les
         champs de l'ENFANT qui referencent ailleurs. */
      const entrants = entrantsDe(lien.door);
      return fiches.map((fiche) => ({
        family: lien.family,
        /* Le sous-type du LIEN, ou rien : cote SDK, le `type` porte par la fiche
           gagne s'il est une chaine non vide (`templateChildFromRecord`). */
        type: lien.type ?? '',
        linkKey: lien.by,
        record: sansChampsDeLien(fiche, entrants),
      }));
    }),
  );
  return parLien.flat();
}
